/* VLX Fluid: Apple-style interaction layer for the Veiluxe Dawn theme (Cofounder, 2026-10-24).
   Principles from Apple's "Designing Fluid Interfaces": respond on touch-down, track the finger 1:1,
   hand the finger's speed to a spring on release, project momentum to pick the resting point,
   resist softly at edges, and let any motion be grabbed mid-flight.
   Settings come from the VLX Fluid section as window.VLXFluid. */
(function () {
  'use strict';

  var cfg = window.VLXFluid || {};
  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var SLOP_PX = 10;              /* movement before a touch counts as a drag (hysteresis) */
  var VELOCITY_WINDOW_MS = 100;  /* only the last 100ms of samples decide release speed */
  var HELD_STILL_MS = 80;        /* finger paused this long before lifting = no flick */

  /* ---------- Physics kit ---------- */

  /* Spring with Apple's two designer parameters: damping (1 = settles with no overshoot,
     below 1 = a little bounce) and response (seconds; lower = snappier). Starts from the
     current value and velocity, so it can be interrupted and re-targeted at any moment. */
  function spring(o) {
    var x = o.from, v = o.velocity || 0, to = o.to;
    var omega = 2 * Math.PI / (o.response || 0.4);
    var stiffness = omega * omega;
    var friction = 2 * (o.damping == null ? 1 : o.damping) * omega;
    var eps = o.epsilon || 0.01;
    var raf = o.raf || window.requestAnimationFrame.bind(window);
    var caf = o.caf || window.cancelAnimationFrame.bind(window);
    var last = 0, id = 0, finished = false;
    var handle = {
      stop: function () { caf(id); },
      value: function () { return x; },
      velocity: function () { return v; },
      finished: function () { return finished; }
    };
    function finish() {
      x = to; v = 0; finished = true;
      o.onUpdate(x);
      if (o.onDone) o.onDone();
    }
    function frame(now) {
      var dt = last ? Math.min((now - last) / 1000, 1 / 30) : 1 / 60;
      last = now;
      for (var i = 0, h = dt / 4; i < 4; i++) { /* sub-steps keep stiff springs stable */
        v += (-stiffness * (x - to) - friction * v) * h;
        x += v * h;
      }
      if (Math.abs(x - to) < eps && Math.abs(v) < eps * 10) return finish();
      o.onUpdate(x);
      id = raf(frame);
    }
    if (o.reduced == null ? REDUCED : o.reduced) finish();
    else id = raf(frame);
    return handle;
  }

  /* Where a flick comes to rest (Apple's projection, exponential decay). rate 0.998 = scroll feel. */
  function project(velocity, rate) {
    rate = rate || 0.998;
    return (velocity / 1000) * rate / (1 - rate);
  }

  /* Soft edge: the further past the bound, the less the element follows. */
  function rubberband(overshoot, dimension, constant) {
    constant = constant || 0.55;
    return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
  }

  /* Release speed from recent pointer samples, in px per second. */
  function Tracker() { this.samples = []; }
  Tracker.prototype.add = function (pos, time) {
    this.samples.push([pos, time]);
    if (this.samples.length > 8) this.samples.shift();
  };
  Tracker.prototype.velocity = function (now) {
    var s = this.samples;
    if (s.length < 2) return 0;
    var newest = s[s.length - 1];
    if (now != null && now - newest[1] > HELD_STILL_MS) return 0;
    var oldest = newest;
    for (var i = s.length - 2; i >= 0 && newest[1] - s[i][1] <= VELOCITY_WINDOW_MS; i--) oldest = s[i];
    var dt = (newest[1] - oldest[1]) / 1000;
    return dt > 0 ? (newest[0] - oldest[0]) / dt : 0;
  };

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  window.VLXFluidKit = { spring: spring, project: project, rubberband: rubberband, Tracker: Tracker, clamp: clamp };

  /* ---------- Glass header that follows the scroll ---------- */
  /* Scrolling down slides the header away exactly as far as the page moves; scrolling up brings it
     back the same way. If scrolling stops halfway, a spring finishes the move in the direction
     the page was last going. Never hides while the menu, search or a header field is in use. */
  function initHeader() {
    var sec = document.querySelector('.section-header');
    if (!sec) return;
    var root = document.documentElement;
    var SETTLE_DELAY_MS = 140;
    var height = 0, start = 0, offset = 0, lastY = window.scrollY, dir = 0, idle = 0, anim = null;

    function measure() {
      height = sec.offsetHeight;
      var prev = sec.previousElementSibling;
      start = prev ? prev.offsetTop + prev.offsetHeight : 0;
    }
    function busy() {
      return sec.classList.contains('menu-open') || !!sec.querySelector('details[open]') ||
        (sec.contains(document.activeElement) && /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName));
    }
    function apply(v) {
      offset = v;
      sec.style.transform = v > 0.5 ? 'translate3d(0,' + (-v).toFixed(2) + 'px,0)' : '';
    }
    function settle() {
      if (offset <= 0 || offset >= height) return;
      anim = spring({ from: offset, to: dir > 0 && !busy() ? height : 0, damping: 1, response: 0.3, epsilon: 0.5,
        onUpdate: apply, onDone: function () { anim = null; } });
    }
    function onScroll() {
      var y = window.scrollY, dy = y - lastY;
      lastY = y;
      if (anim) { anim.stop(); anim = null; }
      root.classList.toggle('vlx-scrolled', y > start);
      if (dy) dir = dy > 0 ? 1 : -1;
      if (busy()) apply(0);
      else if (y <= start + height) apply(Math.min(offset, Math.max(0, y - start)));
      else apply(clamp(offset + dy, 0, height));
      clearTimeout(idle);
      idle = setTimeout(settle, SETTLE_DELAY_MS);
    }

    measure();
    root.classList.add('vlx-glass');
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); apply(clamp(offset, 0, height)); }, { passive: true });
    /* Anything opening inside the header brings it fully back first. */
    sec.addEventListener('toggle', function () { if (anim) anim.stop(); apply(0); }, true);
    onScroll();
  }

  /* ---------- Before/after sliders ---------- */
  /* Drag anywhere on the photo: the divider moves with the finger from where you grabbed it,
     resists softly past the edges, and a flick carries on to where it would naturally stop.
     A tap glides the divider to that spot. Vertical swipes still scroll the page. */
  function initBeforeAfter() {
    Array.prototype.forEach.call(document.querySelectorAll('.ba-wrapper'), function (w) {
      var range = w.querySelector('.ba-range');
      var after = w.querySelector('.ba-after');
      var handle = w.querySelector('.ba-handle');
      if (!range || !after || !handle) return;
      var TAP_MS = 400;
      var value = parseFloat(range.value) || 50, drag = null, anim = null, tracker = new Tracker();

      function render(v) {
        value = v;
        after.style.width = clamp(v, 0, 100) + '%';
        handle.style.left = v + '%';
        range.value = Math.round(clamp(v, 0, 100));
      }
      function current() {
        var shown = parseFloat(after.style.width);
        return isNaN(shown) ? value : shown;
      }
      function withEdges(raw, width) {
        if (raw < 0) return -rubberband(-raw / 100 * width, width) / width * 100;
        if (raw > 100) return 100 + rubberband((raw - 100) / 100 * width, width) / width * 100;
        return raw;
      }
      function glide(target, velocity, damping) {
        anim = spring({ from: value, to: target, velocity: velocity, damping: damping, response: 0.35, epsilon: 0.05,
          onUpdate: render, onDone: function () { anim = null; } });
      }

      w.classList.add('vlx-ba');
      w.addEventListener('pointerdown', function (e) {
        if (e.button > 0) return;
        if (anim) { anim.stop(); anim = null; }
        var rect = w.getBoundingClientRect();
        value = current();
        drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, from: value, left: rect.left, width: rect.width, live: false, t0: e.timeStamp };
        tracker = new Tracker();
        tracker.add(e.clientX, e.timeStamp);
      });
      w.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== drag.id) return;
        var dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
        if (!drag.live) {
          if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
          if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; } /* a scroll, not a drag */
          drag.live = true;
          drag.x0 = e.clientX; /* start tracking from here so nothing jumps */
          try { w.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
        }
        tracker.add(e.clientX, e.timeStamp);
        render(withEdges(drag.from + (e.clientX - drag.x0) / drag.width * 100, drag.width));
      });
      function end(e) {
        if (!drag || e.pointerId !== drag.id) return;
        var d = drag;
        drag = null;
        if (!d.live) {
          if (e.type === 'pointerup' && e.timeStamp - d.t0 < TAP_MS) glide(clamp((e.clientX - d.left) / d.width * 100, 0, 100), 0, 1);
          return;
        }
        var vPx = tracker.velocity(e.timeStamp);
        var vPct = vPx / d.width * 100;
        var target = clamp(value + project(vPct, 0.99), 0, 100);
        glide(target, vPct, Math.abs(vPx) > 400 ? 0.85 : 1); /* bounce only after a real flick */
      }
      w.addEventListener('pointerup', end);
      w.addEventListener('pointercancel', end);
    });
  }

  /* ---------- Swipe the menu closed ---------- */
  /* The open menu follows the finger to the left, resists to the right, and closes if the
     swipe would carry it past halfway. Grabbing it while it springs back takes over at once. */
  function initMenuSwipe() {
    var drawer = document.querySelector('header-drawer');
    var details = drawer && drawer.querySelector('details');
    var panel = details && details.querySelector('.menu-drawer');
    var summary = details && details.querySelector('summary');
    if (!panel || !summary || typeof drawer.closeMenuDrawer !== 'function') return;
    var CLEANUP_MS = 600;
    var x = 0, drag = null, anim = null, tracker = new Tracker();

    function render(v) {
      x = v;
      panel.style.transform = 'translate3d(' + v.toFixed(2) + 'px,0,0)';
    }
    function release() {
      panel.style.transform = '';
      panel.style.transition = '';
      x = 0;
    }

    panel.classList.add('vlx-swipe');
    panel.addEventListener('pointerdown', function (e) {
      if (!details.classList.contains('menu-opening') || e.button > 0) return;
      if (anim) { anim.stop(); anim = null; }
      drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, from: x, width: panel.offsetWidth, live: false };
      tracker = new Tracker();
      tracker.add(e.clientX, e.timeStamp);
    });
    panel.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
      if (!drag.live) {
        if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
        if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; } /* scrolling the menu list */
        drag.live = true;
        drag.x0 = e.clientX;
        panel.style.transition = 'none';
        try { panel.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
      }
      tracker.add(e.clientX, e.timeStamp);
      var raw = drag.from + (e.clientX - drag.x0);
      render(raw > 0 ? rubberband(raw, drag.width) : raw);
    });
    function end(e) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag;
      drag = null;
      if (!d.live) return;
      var vel = tracker.velocity(e.timeStamp);
      /* Normal scroll deceleration decides where the flick would carry the menu. */
      var closing = x + project(vel, 0.998) < -d.width / 2;
      anim = spring({
        from: x, to: closing ? -d.width : 0, velocity: vel,
        damping: closing || Math.abs(vel) < 400 ? 1 : 0.85, response: 0.3, epsilon: 0.5,
        onUpdate: render,
        onDone: function () {
          anim = null;
          if (!closing) return release();
          drawer.closeMenuDrawer(new Event('vlx-swipe'), summary);
          setTimeout(release, CLEANUP_MS); /* after Dawn has finished closing; panel is off-screen by then */
        }
      });
    }
    panel.addEventListener('pointerup', end);
    panel.addEventListener('pointercancel', end);
  }

  function boot() {
    if (cfg.header) initHeader();
    if (cfg.beforeAfter) initBeforeAfter();
    if (cfg.menuSwipe) initMenuSwipe();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();